import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Save,
  Send,
  Calculator,
  Building2,
  User,
  FileText,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency, getStatusClass } from "../lib/utils";
import CategoryManager from "../components/CategoryManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isOfficeManager } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [propertyManagers, setPMs] = useState([]);
  const [services, setServices] = useState([]);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const [formData, setFormData] = useState({
    company_id: "",
    client_id: "",
    property_manager_id: "",
    property_address: "",
    // Manual entry fields
    manual_customer_name: "",
    manual_customer_email: "",
    manual_customer_phone: "",
    strata_plan_number: "",
    num_floors: 1,
    num_units: 1,
    services: [],
    goods: [], // New: goods/products line items
    job_types: [],
    safety_hazards: "",
    piecework_hours: 0,
    quote_ratio: null,
    actual_hours: null,
    on_site_contact: "",
    access_info: "",
    internal_notes: "",
    external_notes: "",
    tax_rate: 0,
    fleet_ids: [],
    equipment_ids: [],
  });

  const [quote, setQuote] = useState(null);
  const [goodsList, setGoodsList] = useState([]); // Available goods from API
  const [jobTypeCategories, setJobTypeCategories] = useState([]); // Customizable job types

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchJobTypes = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/categories/?category_type=job_type`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJobTypeCategories(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to fetch job types");
    }
  };

  const fetchInitialData = async () => {
    try {
      const [companiesRes, clientsRes, servicesRes, fleetRes, equipmentRes, goodsRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/clients`).catch(() => ({ data: [] })),
        axios.get(`${API}/services`),
        axios.get(`${API}/fleet/vehicles`).catch(() => ({ data: [] })),
        axios.get(`${API}/equipment`).catch(() => ({ data: [] })),
        axios.get(`${API}/goods`).catch(() => ({ data: [] })),
      ]);
      setCompanies(companiesRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      setFleetVehicles(fleetRes.data);
      setEquipment(equipmentRes.data);
      setGoodsList(goodsRes.data);
      
      // Fetch job types from categories
      await fetchJobTypes();

      if (id) {
        const quoteRes = await axios.get(`${API}/quotes/${id}`);
        const q = quoteRes.data;
        setQuote(q);
        
        setFormData({
          company_id: q.company_id || "",
          client_id: q.client_id || "",
          property_manager_id: q.property_manager_id || "",
          property_address: q.property_address || "",
          manual_customer_name: q.manual_customer_name || "",
          manual_customer_email: q.manual_customer_email || "",
          manual_customer_phone: q.manual_customer_phone || "",
          strata_plan_number: q.strata_plan_number || "",
          num_floors: q.num_floors || 1,
          num_units: q.num_units || 1,
          services: q.services || [],
          goods: q.goods || [],
          job_types: q.job_types || [],
          safety_hazards: q.safety_hazards || "",
          piecework_hours: q.piecework_hours || 0,
          quote_ratio: q.quote_ratio,
          actual_hours: q.actual_hours,
          on_site_contact: q.on_site_contact || "",
          access_info: q.access_info || "",
          internal_notes: q.internal_notes || "",
          external_notes: q.external_notes || "",
          tax_rate: q.tax_rate != null ? q.tax_rate : 0,
          fleet_ids: q.fleet_ids || [],
          equipment_ids: q.equipment_ids || [],
        });

        // Fetch PMs for the company
        const pmsRes = await axios.get(
          `${API}/property-managers?company_id=${q.company_id}`
        );
        setPMs(pmsRes.data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = async (companyId) => {
    setFormData({ ...formData, company_id: companyId, property_manager_id: "" });
    try {
      const res = await axios.get(
        `${API}/property-managers?company_id=${companyId}`
      );
      setPMs(res.data);
    } catch (error) {
      console.error("Failed to fetch PMs:", error);
    }
  };

  const addService = () => {
    setFormData({
      ...formData,
      services: [
        ...formData.services,
        {
          service_id: "",
          service_name: "",
          quantity: 1,
          rate: 0,
          total: 0,
          notes: "",
          is_internal: false,
        },
      ],
    });
  };

  const updateService = (index, field, value) => {
    const updated = [...formData.services];
    updated[index][field] = value;

    // Auto-calculate total
    if (field === "quantity" || field === "rate") {
      updated[index].total = updated[index].quantity * updated[index].rate;
    }

    // Auto-fill service name and rate when service is selected
    if (field === "service_id") {
      const service = services.find((s) => s.id === value);
      if (service) {
        updated[index].service_name = service.name;
        updated[index].rate = service.base_rate;
        updated[index].total = updated[index].quantity * service.base_rate;
      }
    }

    setFormData({ ...formData, services: updated });
  };

  const removeService = (index) => {
    setFormData({
      ...formData,
      services: formData.services.filter((_, i) => i !== index),
    });
  };

  // Goods management functions
  const addGood = () => {
    setFormData({
      ...formData,
      goods: [
        ...formData.goods,
        {
          good_id: "",
          good_name: "",
          quantity: 1,
          unit_price: 0,
          total: 0,
          notes: "",
        },
      ],
    });
  };

  const updateGood = (index, field, value) => {
    const updated = [...formData.goods];
    updated[index][field] = value;

    // Auto-calculate total
    if (field === "quantity" || field === "unit_price") {
      updated[index].total = updated[index].quantity * updated[index].unit_price;
    }

    // Auto-fill good name and price when good is selected
    if (field === "good_id") {
      const good = goodsList.find((g) => g.id === value);
      if (good) {
        updated[index].good_name = good.name;
        updated[index].unit_price = good.unit_price;
        updated[index].total = updated[index].quantity * good.unit_price;
      }
    }

    setFormData({ ...formData, goods: updated });
  };

  const removeGood = (index) => {
    setFormData({
      ...formData,
      goods: formData.goods.filter((_, i) => i !== index),
    });
  };

  // Calculate totals
  const servicesTotal = formData.services.reduce((sum, s) => sum + (s.total || 0), 0);
  const goodsTotal = formData.goods.reduce((sum, g) => sum + (g.total || 0), 0);
  const grandTotal = servicesTotal + goodsTotal;

  const toggleJobType = (jobType) => {
    const current = formData.job_types;
    const updated = current.includes(jobType)
      ? current.filter((t) => t !== jobType)
      : [...current, jobType];
    setFormData({ ...formData, job_types: updated });
  };

  const toggleFleet = (fleetId) => {
    setFormData((prev) => {
      const current = prev.fleet_ids || [];
      const updated = current.includes(fleetId)
        ? current.filter((id) => id !== fleetId)
        : [...current, fleetId];
      return { ...prev, fleet_ids: updated };
    });
  };

  const toggleEquipment = (equipmentId) => {
    setFormData((prev) => {
      const current = prev.equipment_ids || [];
      const updated = current.includes(equipmentId)
        ? current.filter((id) => id !== equipmentId)
        : [...current, equipmentId];
      return { ...prev, equipment_ids: updated };
    });
  };

  const calculateTotal = () => {
    const servicesSubtotal = formData.services
      .filter((s) => !s.is_internal)
      .reduce((sum, s) => sum + s.total, 0);
    const goodsSubtotal = formData.goods.reduce((sum, g) => sum + g.total, 0);
    return servicesSubtotal + goodsSubtotal;
  };

  const calculatePieceworkHours = () => {
    // Simple estimation: total / 50 (hourly rate assumption)
    const total = calculateTotal();
    const hours = total / 50;
    setFormData({ ...formData, piecework_hours: Math.round(hours * 10) / 10 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Convert services and goods into items for the backend
      const items = [
        ...formData.services.filter(s => s.service_name || s.service_id).map(s => ({
          service_id: s.service_id || null,
          description: s.service_name || "Service",
          quantity: s.quantity || 1,
          unit_price: s.rate || 0,
          total_price: s.total || (s.quantity * s.rate) || 0,
        })),
        ...formData.goods.filter(g => g.good_name || g.good_id).map(g => ({
          service_id: null,
          description: g.good_name || "Product",
          quantity: g.quantity || 1,
          unit_price: g.unit_price || 0,
          total_price: g.total || (g.quantity * g.unit_price) || 0,
        })),
      ];

      const payload = {
        company_id: formData.company_id || null,
        client_id: formData.client_id || null,
        property_address: formData.property_address || null,
        manual_customer_name: formData.manual_customer_name || null,
        manual_customer_email: formData.manual_customer_email || null,
        manual_customer_phone: formData.manual_customer_phone || null,
        notes: formData.external_notes || null,
        internal_notes: formData.internal_notes || null,
        piecework_hours: formData.piecework_hours || null,
        tax_rate: parseFloat(formData.tax_rate) || 0,
        equipment_ids: formData.equipment_ids?.length ? formData.equipment_ids : null,
        fleet_ids: formData.fleet_ids?.length ? formData.fleet_ids : null,
        items,
      };

      if (isEditing) {
        await axios.put(`${API}/quotes/${id}`, payload);
        toast.success("Quote updated");
      } else {
        const res = await axios.post(`${API}/quotes`, payload);
        toast.success("Quote created");
        navigate(`/quotes/${res.data.id}`);
        return;
      }
      navigate(`/quotes/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      setQuote({ ...quote, status: newStatus });
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="quote-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {isEditing ? `Edit Quote` : "Create New Quote"}
          </h1>
          {quote && (
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-sm">{quote.quote_number}</span>
              <Badge variant="outline" className={getStatusClass(quote.status)}>
                {quote.status}
              </Badge>
            </div>
          )}
        </div>
        {isEditing && isOfficeManager && (
          <Select value={quote?.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-40" data-testid="status-select">
              <SelectValue placeholder="Change status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="requested">Requested</SelectItem>
              <SelectItem value="current">Current</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="callback">Callback</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Information */}
        <Card>
          <CardHeader>
            <CardTitle>Client Information</CardTitle>
            <CardDescription>Link to company, client, and/or enter client details manually (all optional)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Selection */}
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <Label className="text-base font-medium">Company</Label>
                <span className="text-xs text-muted-foreground">(optional)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Select Company</Label>
                  <Select
                    value={formData.company_id || "none"}
                    onValueChange={(v) => {
                      const newCompanyId = v === "none" ? "" : v;
                      setFormData({ ...formData, company_id: newCompanyId, property_manager_id: "" });
                      if (newCompanyId) {
                        handleCompanyChange(newCompanyId);
                      } else {
                        setPMs([]);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Property Manager</Label>
                  <Select
                    value={formData.property_manager_id || "none"}
                    onValueChange={(v) =>
                      setFormData({ ...formData, property_manager_id: v === "none" ? "" : v })
                    }
                    disabled={!formData.company_id}
                  >
                    <SelectTrigger data-testid="pm-select">
                      <SelectValue placeholder="Select PM" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {propertyManagers.map((pm) => (
                        <SelectItem key={pm.id} value={pm.id}>
                          {pm.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Client Selection */}
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <Label className="text-base font-medium">Client</Label>
                <span className="text-xs text-muted-foreground">(optional)</span>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Select Client</Label>
                <Select
                  value={formData.client_id || "none"}
                  onValueChange={(v) =>
                    setFormData({ ...formData, client_id: v === "none" ? "" : v })
                  }
                >
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} {c.company_name ? `(${c.company_name})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Manual Client Entry */}
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <Label className="text-base font-medium">Manual Client Entry</Label>
                <span className="text-xs text-muted-foreground">(optional - use if client not in system)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Client Name</Label>
                  <Input
                    value={formData.manual_customer_name}
                    onChange={(e) =>
                      setFormData({ ...formData, manual_customer_name: e.target.value })
                    }
                    placeholder="John Doe or Company Name"
                    data-testid="manual-customer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Email</Label>
                  <Input
                    type="email"
                    value={formData.manual_customer_email}
                    onChange={(e) =>
                      setFormData({ ...formData, manual_customer_email: e.target.value })
                    }
                    placeholder="email@example.com"
                    data-testid="manual-customer-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Phone</Label>
                  <Input
                    value={formData.manual_customer_phone}
                    onChange={(e) =>
                      setFormData({ ...formData, manual_customer_phone: e.target.value })
                    }
                    placeholder="(555) 123-4567"
                    data-testid="manual-customer-phone"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Property Information */}
        <Card>
          <CardHeader>
            <CardTitle>Property Information</CardTitle>
            <CardDescription>Details about the job site (all fields optional)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Property Address</Label>
              <Input
                value={formData.property_address}
                onChange={(e) =>
                  setFormData({ ...formData, property_address: e.target.value })
                }
                placeholder="123 Main St, Vancouver, BC (optional)"
                data-testid="address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Strata Plan #</Label>
                <Input
                  value={formData.strata_plan_number}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      strata_plan_number: e.target.value,
                    })
                  }
                  placeholder="BCS1234"
                />
              </div>
              <div className="space-y-2">
                <Label># of Floors</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.num_floors}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      num_floors: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label># of Units</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.num_units}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      num_units: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Services</CardTitle>
              <CardDescription>Add services to this quote</CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addService} data-testid="add-service-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Service
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.services.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                No services added yet. Click "Add Service" to begin.
              </p>
            ) : (
              formData.services.map((service, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg space-y-4"
                  data-testid={`service-item-${index}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Service</Label>
                        <Select
                          value={service.service_id}
                          onValueChange={(v) =>
                            updateService(index, "service_id", v)
                          }
                        >
                          <SelectTrigger data-testid={`quote-service-select-${index}`}>
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((s) => (
                              <SelectItem
                                key={s.id}
                                value={s.id}
                                data-testid={`quote-service-option-${s.id}`}
                              >
                                {/* iter141 (Beta P2 — "generic service names hard
                                    to select"): show name + rate + unit + category
                                    on one line so testers can differentiate
                                    similarly-named services at a glance. */}
                                <span className="flex flex-col">
                                  <span className="font-medium">{s.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ${Number(s.base_rate ?? s.default_price ?? 0).toFixed(2)}
                                    {s.unit || s.price_unit ? ` · ${(s.unit || s.price_unit).replace(/_/g, " ")}` : ""}
                                    {s.category ? ` · ${s.category}` : ""}
                                  </span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={service.quantity}
                          onChange={(e) =>
                            updateService(
                              index,
                              "quantity",
                              parseInt(e.target.value) || 1
                            )
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rate ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={service.rate}
                          onChange={(e) =>
                            updateService(
                              index,
                              "rate",
                              parseFloat(e.target.value) || 0
                            )
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Total</Label>
                        <div className="h-10 flex items-center font-semibold">
                          {formatCurrency(service.total)}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeService(index)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`internal-${index}`}
                        checked={service.is_internal}
                        onCheckedChange={(checked) =>
                          updateService(index, "is_internal", checked)
                        }
                      />
                      <Label htmlFor={`internal-${index}`} className="text-sm">
                        Internal only (won't show on client quote)
                      </Label>
                    </div>
                  </div>
                  <Input
                    placeholder="Notes for this service..."
                    value={service.notes}
                    onChange={(e) =>
                      updateService(index, "notes", e.target.value)
                    }
                  />
                </div>
              ))
            )}

            {formData.services.length > 0 && (
              <div className="flex justify-end pt-4 border-t">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Services Subtotal</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(servicesTotal)}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Goods / Products */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Goods & Products</CardTitle>
              <CardDescription>Add materials, supplies, or products to this quote</CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addGood}>
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.goods.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                No items added yet. Click "Add Item" to include products or materials.
              </p>
            ) : (
              formData.goods.map((good, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg space-y-4"
                  data-testid={`good-item-${index}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Product/Item</Label>
                        <Select
                          value={good.good_id}
                          onValueChange={(v) => updateGood(index, "good_id", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                          <SelectContent>
                            {goodsList.map((g) => (
                              <SelectItem key={g.id} value={g.id}>
                                {g.name} - {formatCurrency(g.unit_price)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={good.quantity}
                          onChange={(e) =>
                            updateGood(index, "quantity", parseInt(e.target.value) || 1)
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Unit Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={good.unit_price}
                          onChange={(e) =>
                            updateGood(index, "unit_price", parseFloat(e.target.value) || 0)
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Total</Label>
                        <div className="h-10 flex items-center px-3 bg-muted rounded-md font-medium">
                          {formatCurrency(good.total)}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeGood(index)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Input
                    placeholder="Notes for this item..."
                    value={good.notes}
                    onChange={(e) => updateGood(index, "notes", e.target.value)}
                  />
                </div>
              ))
            )}

            {formData.goods.length > 0 && (
              <div className="flex justify-end pt-4 border-t">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Goods Subtotal</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(goodsTotal)}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Grand Total */}
        {(formData.services.length > 0 || formData.goods.length > 0) && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 space-y-4">
              {/* iter141: Tax rate field (Beta feedback P2 — "No option to add
                  tax outside of the live editor"). Live-recomputes the grand
                  total so the tester sees the tax impact before saving. */}
              <div className="flex items-center gap-4">
                <Label className="text-sm w-32" htmlFor="quote-tax-rate">Tax Rate (%)</Label>
                <Input
                  id="quote-tax-rate"
                  data-testid="quote-tax-rate"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  className="w-32"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                  placeholder="0"
                />
                <span className="text-xs text-muted-foreground">
                  Applied to subtotal at save. Leave 0 for tax-exempt customers.
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <div>
                  <p className="text-lg font-medium">Quote Total</p>
                  <p className="text-sm text-muted-foreground">
                    {formData.services.length} service(s), {formData.goods.length} item(s)
                    {parseFloat(formData.tax_rate) > 0 ? ` · +${formData.tax_rate}% tax` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold text-primary">
                    {formatCurrency(grandTotal * (1 + (parseFloat(formData.tax_rate) || 0) / 100))}
                  </p>
                  {parseFloat(formData.tax_rate) > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Subtotal {formatCurrency(grandTotal)} + Tax {formatCurrency(grandTotal * (parseFloat(formData.tax_rate) || 0) / 100)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fleet & Equipment */}
        {(fleetVehicles.length > 0 || equipment.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle>Fleet & Equipment</CardTitle>
              <CardDescription>
                Select vehicles and equipment required for this job
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {fleetVehicles.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-medium">Fleet Vehicles</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {fleetVehicles.map((vehicle) => (
                      <div
                        key={vehicle.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.fleet_ids.includes(vehicle.id)
                            ? "bg-primary/10 border-primary"
                            : "hover:bg-muted"
                        }`}
                        onClick={() => toggleFleet(vehicle.id)}
                        data-testid={`quote-fleet-toggle-${vehicle.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Checkbox
                            checked={formData.fleet_ids.includes(vehicle.id)}
                            onCheckedChange={() => toggleFleet(vehicle.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1"
                          />
                          <div>
                            <p className="font-medium text-sm">{vehicle.make} {vehicle.model}</p>
                            <p className="text-xs text-muted-foreground">
                              {vehicle.license_plate || vehicle.year || "No plate"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {equipment.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-medium">Equipment</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {equipment.map((item) => (
                      <div
                        key={item.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.equipment_ids.includes(item.id)
                            ? "bg-primary/10 border-primary"
                            : "hover:bg-muted"
                        }`}
                        onClick={() => toggleEquipment(item.id)}
                        data-testid={`quote-equipment-toggle-${item.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Checkbox
                            checked={formData.equipment_ids.includes(item.id)}
                            onCheckedChange={() => toggleEquipment(item.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1"
                          />
                          <div>
                            <p className="font-medium text-sm">{item.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.category || item.serial_number || "No category"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.fleet_ids.length > 0 || formData.equipment_ids.length > 0 ? (
                <div className="pt-2 text-sm text-muted-foreground">
                  Selected: {formData.fleet_ids.length} vehicle(s), {formData.equipment_ids.length} equipment
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Job Details */}
        <Card>
          <CardHeader>
            <CardTitle>Job Details</CardTitle>
            <CardDescription>
              Job type, safety information, and hours
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Job Types</Label>
                <CategoryManager
                  categoryType="job_type"
                  label="Job Types"
                  compact={true}
                  onCategoriesChange={fetchJobTypes}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {jobTypeCategories.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No job types defined. Click the settings icon to add some.
                  </p>
                ) : (
                  jobTypeCategories.map((type) => (
                    <Button
                      key={type.id}
                      type="button"
                      variant={
                        formData.job_types.includes(type.name)
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => toggleJobType(type.name)}
                      style={
                        formData.job_types.includes(type.name)
                          ? { backgroundColor: type.color, borderColor: type.color }
                          : { borderColor: type.color, color: type.color }
                      }
                      className="transition-all"
                    >
                      {type.name}
                    </Button>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Safety Hazards</Label>
              <Textarea
                value={formData.safety_hazards}
                onChange={(e) =>
                  setFormData({ ...formData, safety_hazards: e.target.value })
                }
                placeholder="List any safety hazards..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Piecework Hours
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={calculatePieceworkHours}
                    title="Auto-calculate"
                  >
                    <Calculator className="h-4 w-4" />
                  </Button>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.piecework_hours}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      piecework_hours: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Quote Ratio</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.quote_ratio || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      quote_ratio: parseFloat(e.target.value) || null,
                    })
                  }
                  placeholder="e.g., 1.3"
                />
              </div>
              <div className="space-y-2">
                <Label>Actual Hours (after completion)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.actual_hours || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      actual_hours: parseFloat(e.target.value) || null,
                    })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact & Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Contact & Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>On-Site Contact</Label>
                <Input
                  value={formData.on_site_contact}
                  onChange={(e) =>
                    setFormData({ ...formData, on_site_contact: e.target.value })
                  }
                  placeholder="Name and phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Access Information</Label>
                <Input
                  value={formData.access_info}
                  onChange={(e) =>
                    setFormData({ ...formData, access_info: e.target.value })
                  }
                  placeholder="Keys, codes, etc."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>External Notes (visible to client)</Label>
              <Textarea
                value={formData.external_notes}
                onChange={(e) =>
                  setFormData({ ...formData, external_notes: e.target.value })
                }
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Internal Notes (office use only)</Label>
              <Textarea
                value={formData.internal_notes}
                onChange={(e) =>
                  setFormData({ ...formData, internal_notes: e.target.value })
                }
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-quote-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            {isEditing ? "Update Quote" : "Create Quote"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuoteForm;
